<template>
	<DataDialog ref="dialog" 
		width="80%" 
		height="900" 
		:title="dialogTitle"
		v-model="model"
		:onConfirm="handleConfirm"
		:confirmLabel="confirmLabel"
		:cancelLabel="$t('text.close')"
		:validator="validateModel"
		:disableConfirmButton="disableConfirmButton"
		@close="handleDialogClosed"
	>
		<template #content="{ model }">
			<div >
				<!-- Step 1: Warning and Configuration -->
				<div v-if="currentStep === 'configure'" style="display: flex; flex-direction: column; gap: 16px;">
					<v-alert 
						type="warning" 
						variant="tonal"
						border="start"
						class="warning-alert"
					>
						<div class="warning-text">
							<div class="warning-title">{{ $t('text.bulkOrderCancellationWarningTitle') }}</div>
							<div class="warning-message">{{ $t('text.bulkOrderCancellationWarningMessage') }}</div>
						</div>
					</v-alert>

					<div class="order-info">
						<div class="info-inline">
							<span class="info-label">{{ $t('text.affectedTimeSlot') }}:</span>
							<span class="info-value">{{ formatDate(selectedDay?.date) }} {{ $t('text.at') }} {{ selectedSlot?.time || $t('text.allDay') }}</span>
						</div>
					</div>

					<div class="options-section">
						<v-checkbox 
							v-model="model.cancelContingent" 
							color="warning"
							density="compact"
						>
							<template #label>
								<div>
									<div style="font-weight: 500;">{{ $t('text.cancelContingentForSlot') }}</div>
									<div style="font-size: 12px; color: #666; margin-top: 2px;">
										{{ $t('text.cancelContingentDescription') }}
									</div>
								</div>
							</template>
						</v-checkbox>
						
						<v-checkbox 
							v-model="model.skipContractorSystem" 
							:disabled="model.checkForConsumedItems"
							color="primary"
							density="compact"
						>
							<template #label>
								<div>
									<div style="font-weight: 500;">{{ $t('text.skipCancellationOfOrderItems') }}</div>
								</div>
							</template>
						</v-checkbox>
						
						<v-checkbox 
							v-model="model.checkForConsumedItems"
							:disabled="model.skipContractorSystem"
							color="primary"
							density="compact"
						>
							<template #label>
								<div>
									<div style="font-weight: 500;">{{ $t('text.checkForConsumedItems') }}</div>
								</div>
							</template>
						</v-checkbox>
					</div>
				</div>

				<!-- Step: Restart Configuration -->
				<div v-if="currentStep === 'restart-config'" style="display: flex; flex-direction: column; gap: 16px;">
					<v-alert 
						type="info" 
						variant="tonal"
						border="start"
					>
						<div class="warning-text">
							<div class="warning-title">{{ $t('text.restartFailedItemsTitle') }}</div>
							<div class="warning-message">{{ $t('text.restartFailedItemsMessage', { count: currentJob?.statusData?.summary?.failedCancellations || 0 }) }}</div>
						</div>
					</v-alert>

					<div class="order-info">
						<div class="info-inline">
							<span class="info-label">{{ $t('text.affectedTimeSlot') }}:</span>
							<span class="info-value">{{ formatDate(selectedDay?.date) }} {{ $t('text.at') }} {{ selectedSlot?.time || $t('text.allDay') }}</span>
						</div>
					</div>

					<div class="options-section">
						<div style="font-size: 14px; color: #666; margin-bottom: 12px; padding: 12px; background: #f5f5f5; border-radius: 4px;">
							<v-icon size="18" color="#1976d2" style="margin-right: 8px;">mdi-information</v-icon>
							{{ $t('text.restartConfigurationHint') }}
						</div>
						
						<v-checkbox 
							v-model="model.skipContractorSystem" 
							:disabled="model.checkForConsumedItems"
							color="primary"
							density="compact"
						>
							<template #label>
								<div>
									<div style="font-weight: 500;">{{ $t('text.skipCancellationOfOrderItems') }}</div>
									
								</div>
							</template>
						</v-checkbox>
						
						<v-checkbox 
							v-model="model.checkForConsumedItems"
							:disabled="model.skipContractorSystem"
							color="primary"
							density="compact"
						>
							<template #label>
								<div>
									<div style="font-weight: 500;">{{ $t('text.checkForConsumedItems') }}</div>
									
								</div>
							</template>
						</v-checkbox>
					</div>
				</div>

				<!-- Step 2: Order Items List -->
				<div v-if="currentStep === 'orders'" style="display: flex; flex-direction: column; gap: 12px;">
					<div class="orders-header" style="display: flex; justify-content: space-between; align-items: flex-start;">
						<div>
							<h3 style="margin: 0 0 8px 0;">{{ $t('text.ordersToCancel') }}</h3>
							<p style="margin: 0; color: #666; font-size: 14px;">
								{{ $t('text.orderItemsFound', {
									count: orderItems.length,
									date: formatDate(selectedDay?.date),
									time: selectedSlot?.time || $t('text.allDay')
								}) }}
							</p>
						</div>

						<!-- Excel Export Button -->
						<v-btn
							v-if="orderItems?.length > 0 && currentJob?.status === 'DONE'"
							class="export-excel-btn"
							color="success"
							variant="elevated"
							size="small"
							@click="downloadJobResultsExcel"
							:disabled="loadingOrders"
							elevation="2"
							style="min-width: 140px;"
						>
							<v-icon left size="18">mdi-file-excel</v-icon>
							{{ $t('text.downloadAsExcel') }}
						</v-btn>
					</div>


					<div class="orders-list">
						<div class="orders-container">
							<div
								v-for="(item, index) in orderItems"
								:key="item.order_item_id"
								class="order-item"
								:class="getOrderItemClass(item)"
							>
								<div 
									class="order-summary" 
									@click="toggleItemExpansion(index)"
									style="cursor: pointer; display: flex; align-items: center; justify-content: space-between; padding: 12px 16px;"
								>
									<div class="summary-content" style="display: flex; align-items: center; gap: 12px; flex: 1;">
										<div class="order-id" style="font-weight: 600; min-width: 80px;">{{ item.order_id }}</div>
										<div class="customer-name" style="flex: 1;">{{ getCustomerName(item) }}</div>
										<div class="price" style="font-weight: 500;">{{ formatPrice(item.price, item.currency) }}</div>
									</div>
									<div style="display: flex; align-items: center; gap: 8px;">
										<v-chip 
											:color="getStatusColor(getOrderItemStatus(item))"
											:variant="getOrderItemStatus(item) === 'pending' ? 'outlined' : 'flat'"
											size="small"
										>
											<v-icon 
												v-if="getOrderItemStatus(item) === 'processing'" 
												size="16" 
												style="margin-right: 4px;"
											>
												mdi-loading mdi-spin
											</v-icon>
											{{ getStatusLabel(getOrderItemStatus(item)) }}
										</v-chip>
										<v-icon 
											size="20" 
											:style="{ transform: expandedItems[index] ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }"
										>
											mdi-chevron-down
										</v-icon>
									</div>
								</div>
								
								<v-expand-transition>
									<div v-if="expandedItems[index]" class="order-details-expanded" style="padding: 0 16px 16px 16px; border-top: 1px solid #eee;">
										<div class="order-details" style="padding-top: 12px;">
											<div class="detail-row">
												<span class="label">{{ $t('text.itemId') }}:</span>
												<span class="value">{{ item.order_item_id }}</span>
											</div>
											<div class="detail-row">
												<span class="label">{{ $t('text.product') }}:</span>
												<span class="value">{{ item.product_name }}</span>
											</div>
											<div class="detail-row">
												<span class="label">{{ $t('text.customer') }}:</span>
												<span class="value">{{ getCustomerName(item) }}</span>
											</div>
											<div class="detail-row">
												<span class="label">{{ $t('text.orderDate') }}:</span>
												<span class="value">{{ formatDate(item.order_date) }}</span>
											</div>
											<div class="detail-row">
												<span class="label">{{ $t('text.payment') }}:</span>
												<span class="value">{{ formatPaymentInfo(item) }}</span>
											</div>
										<div v-if="getOrderItemStatus(item) === 'failed'" style="margin-top: 12px;">
											<div class="detail-row error-row">
												<span class="label">{{ $t('text.errorCode') }}:</span>
												<span class="value error-text">{{ getTaskErrorCode(item) }}</span>
											</div>
											<div class="detail-row error-row">
												<span class="label">{{ $t('text.errorMessage') }}:</span>
												<span class="value error-text">{{ getTaskErrorMessage(item) }}</span>
											</div>
										</div>
										</div>
									</div>
								</v-expand-transition>
							</div>
							
							<div v-if="!orderItems.length && !loadingOrders" class="no-orders">
								<v-icon size="48" color="grey">mdi-calendar-remove</v-icon>
								<p style="margin: 16px 0 0 0; color: #666;">{{ $t('text.noOrdersFound') }}</p>
							</div>
							
							<div v-if="loadingOrders" class="loading-orders">
								<v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
								<p style="margin: 16px 0 0 0; color: #666;">{{ $t('text.loadingOrders') }}</p>
							</div>
						</div>
					</div>

					<div v-if="currentJob?.status === 'ERROR'" class="error-section">
						<v-alert type="error" variant="tonal">
							<div style="font-weight: 500; margin-bottom: 8px;">{{ $t('text.jobFailed') }}</div>
							<div style="font-size: 14px;">{{ currentJob.statusData?.error || $t('text.unknownErrorOccurred') }}</div>
						</v-alert>
					</div>

					<!-- Completion Summary / Restart Section -->
					<div v-if="currentJob?.status === 'DONE'" class="completion-summary">
						<!-- Success Case: All items cancelled successfully -->
						<v-alert 
							v-if="jobWasSuccessful"
							type="success" 
							variant="tonal"
							style="margin-top: 20px;"
						>
							<div style="font-weight: 500; margin-bottom: 8px;">
								{{ $t('text.allOrdersCancelledSuccessfully') }}
							</div>
							<div style="font-size: 14px;">
								{{ currentJob.statusData.successfulCancellations || 0 }} {{ $t('text.successful') }}, 
								{{ currentJob.statusData.failedCancellations || 0 }} {{ $t('text.failed') }}
							</div>
						</v-alert>

						<!-- Failure Case: Show restart action inline -->
						<div v-else-if="hasNonCancelledItems && hasFailedItems" class="restart-action-section">
							<div 
								class="restart-action-card"
								@click="handleRestartClick"
							>
								<div class="restart-action-content">
									<div class="restart-action-icon">
										<v-icon size="32" color="#ff9800">mdi-restart</v-icon>
									</div>
									<div class="restart-action-text">
										<div class="restart-action-title">
											{{ currentJob.statusData.summary?.failedCancellations || 0 }} {{ $t('text.orderItemsFailedToCancel') }}
										</div>
										<div class="restart-action-description">
											{{ $t('text.clickHereToRestartFailedItems') }}
										</div>
									</div>
									<div class="restart-action-arrow">
										<v-icon size="24" color="#999">mdi-chevron-right</v-icon>
									</div>
								</div>
								<div class="restart-action-stats">
									<div class="stat-item success">
										<span class="stat-number">{{ currentJob.statusData.summary?.successfulCancellations || 0 }}</span>
										<span class="stat-label">{{ $t('text.successful') }}</span>
									</div>
									<div class="stat-divider"></div>
									<div class="stat-item error">
										<span class="stat-number">{{ currentJob.statusData.summary?.failedCancellations || 0 }}</span>
										<span class="stat-label">{{ $t('text.failed') }}</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</template>
		
		<template #buttons-left>
			<div v-if="isJobRunning" class="footer-progress" style="display: flex; align-items: center; gap: 16px; min-width: 300px;">
				<div style="display: flex; flex-direction: column; gap: 4px; flex: 1;">
					<div style="display: flex; justify-content: space-between; align-items: center;">
						<span style="font-weight: 500; font-size: 14px;">{{ currentJob?.statusData?.message?.[selectedLocale] || currentJob?.statusData?.message || $t('text.processing') }}</span>
						<span style="font-weight: 500; color: #1976d2; font-size: 14px;">
							{{ currentJob?.statusData?.progress || 0 }}%
						</span>
					</div>
					<v-progress-linear
						:model-value="currentJob?.statusData?.progress || 0"
						color="primary"
						height="6"
						rounded
					/>
				</div>
			</div>
		</template>
	</DataDialog>
</template>

<script>
import DataDialog from '../../common/DataDialog.vue'
import Common from '@/mixins/Common.vue'
import BackgroundJobExecutive from '../../../../../api2/src/modules/jobs/BackgroundJobExecutive'
import BulkCancellationExecutive from '../../../../../api2/src/modules/bulk-cancellation/BulkCancellationExecutive'
import Loading from '../../../mixins/Loading.vue'

export default {
	components: { DataDialog },
	mixins: [Common, Loading],
	props: {
		product: Object,
		selectedDay: Object,
		selectedSlot: Object,
		getOrderItems: Function,
		orderItems: {
			type: Array,
			default: () => []
		},
		currentJob: {
			type: Object,
			default: null
		}
	},
	data() {
		return {
			model: {
				skipContractorSystem: false,
				checkForConsumedItems: true,
				cancelContingent: true
			},
			currentStep: 'configure', // configure, orders, restart-config
			expandedItems: {}, // Track which items are expanded
			backgroundJobExecutive: new BackgroundJobExecutive(this),
			loadingOrders: false
		}
	},
	computed: {
		isJobRunning() {
			// Compute from the prop passed from parent
			return this.currentJob?.status === 'NEW' || this.currentJob?.status === 'RUNNING'
		},
		dialogTitle() {
			switch (this.currentStep) {
				case 'configure':
					return this.$t('text.bulkOrderCancellation')
				case 'restart-config':
					return this.$t('text.restartFailedItemsTitle')
				case 'orders':
					return this.$t('text.cancelOrdersTitle', {
						date: this.formatDate(this.selectedDay?.date),
						time: this.selectedSlot?.time || this.$t('text.allDay')
					})
				default:
					return this.$t('text.cancelOrders')
			}
		},
		confirmLabel() {
			switch (this.currentStep) {
				case 'configure':
					return this.$t('text.continue')
				case 'restart-config':
					return this.$t('text.startRestartJob')
				case 'orders':
					if (this.isJobRunning) {
						return this.$t('text.jobRunning')
					} else if (this.isRestartMode) {
						// This shouldn't show anymore - user should click the restart card
						return null
					} else {
						return this.$t('text.startCancellationJob')
					}
				default:
					return this.$t('text.confirm')
			}
		},
		isRestartMode() {
			// Check if we're in restart mode (job exists with failed items)
			return this.currentJob && this.currentJob.statusData?.summary?.failedCancellations > 0 && !this.isJobRunning
		},
		disableConfirmButton() {
			if (this.currentStep === 'configure' || this.currentStep === 'restart-config') {
				return false
			}
			if (this.currentStep === 'orders') {
				// Disable if job is running, no order items, or loading
				if (this.isJobRunning || this.orderItems.length === 0 || this.loadingOrders) {
					return true
				}

				// Disable if no non-cancelled items
				if (this.currentJob?.status === 'DONE' && !this.hasNonCancelledItems) {
					return true
				}

				// If job is completed, disable the confirm button (user should click the restart card instead)
				if (this.currentJob && 
					(this.currentJob.status === 'DONE' || this.currentJob.status === 'ERROR')) {
					return true
				}
				
				return false
			}
			return false
		},
		jobWasSuccessful() {
			if (!this.currentJob?.statusData?.summary) return false
			return this.currentJob.statusData.summary.failedCancellations === 0
		},
		hasNonCancelledItems() {
			// Check if there are any order items that are not yet cancelled
			return this.orderItems?.length > 0 && this.orderItems.some(item => item.status?.refunded === 0)
		},
		hasFailedItems() {
			return this.currentJob?.statusData?.summary?.failedCancellations > 0
		},
	},
	methods: {
		async open() {
			this.model = {
				skipContractorSystem: false,
				checkForConsumedItems: true,
				cancelContingent: true
			}
			this.currentStep = 'configure'
			this.expandedItems = {}
			this.$refs.dialog.open()
		},
		async openForMonitoring() {
			// Open dialog to monitor an existing job
			// Note: job comes from parent via prop (currentJob), we just update UI state
			this.model = {
				skipContractorSystem: false,
				checkForConsumedItems: true,
				cancelContingent: true
			}
			this.currentStep = 'orders'
			this.expandedItems = {}

			this.$refs.dialog.open()

			// Parent handles polling, isJobRunning is computed from prop
		},
		async openForRestart(job) {
			// Open dialog to restart a job with only failed items
			// Start at 'restart-config' step to allow user to modify flags
			this.model = {
				skipContractorSystem: job.configData?.skipContractorSystem || false,
				checkForConsumedItems: !job.configData?.forceRefund && !job.configData?.skipContractorSystem,
				cancelContingent: false // Don't re-cancel contingent on restart
			}
			this.currentStep = 'restart-config'
			this.expandedItems = {}

			this.$refs.dialog.open()

			// isJobRunning is computed from prop - job is not running yet until user clicks restart
		},
		toggleItemExpansion(index) {
			this.expandedItems = {
				...this.expandedItems,
				[index]: !this.expandedItems[index]
			}
		},
		validateModel(model) {
			// No validation errors for now
			return null
		},
		async handleConfirm(model) {
			if (this.currentStep === 'configure') {
				// Move to orders step
				this.currentStep = 'orders'
				return false // Don't close dialog
			} else if (this.currentStep === 'restart-config') {
				// Start the restart job immediately with the configured settings
				await this.startCancellationJob(model)
				// Move to orders step to show live progress
				this.currentStep = 'orders'
				return false // Don't close dialog
			} else if (this.currentStep === 'orders') {
				// Start the cancellation job
				await this.startCancellationJob(model)
				return false // Don't close dialog
			}
		},
		async startCancellationJob(model) {
			await this.loadingOverlay(async () => {
				try {
					let ordersToProcess = this.orderItems

				// If we're in restart mode, filter to only failed items
				if (this.isRestartMode && this.currentJob?.statusData?.tasks) {
					const failedTaskIds = this.currentJob.statusData.tasks
						.filter(task => task.status === 'ERROR')
						.map(task => task.orderItemId)

					ordersToProcess = this.orderItems.filter(item => {
						const itemId = parseInt(item.order_item_id || item.id, 10)
						return failedTaskIds.includes(itemId)
					})

					console.log(`Restart mode: Processing ${ordersToProcess.length} failed items out of ${this.orderItems.length} total`)
					console.log('Failed task IDs:', failedTaskIds)
					console.log('Filtered order items:', ordersToProcess)
				}

				// Convert order items to the format expected by the API
				const orders = this.convertOrderItemsToOrders(ordersToProcess)
				console.log('Starting job with orders:', orders)

				// Get clientId from product's client assignment
				const clientId = this.product?.fields?.clientAssignments?.de?.[0]?.fields?.client?.de?.sys?.id
				if (!clientId) {
					throw new Error('Client ID not found in product configuration')
				}

				const jobData = {
					productId: this.product.sys.id,
					clientId: clientId,
					date: this.selectedDay.date,
					timeSlot: this.selectedSlot.time,
					orders: orders,
					skipContractorSystem: model.skipContractorSystem,
					checkForConsumedItems: model.checkForConsumedItems,
					cancelContingent: model.cancelContingent
				}

				// If restarting, include the existing job ID
				if (this.isRestartMode && this.currentJob) {
					jobData.existingJobId = this.currentJob.jobId
				}

					const response = await this.backgroundJobExecutive.startCancelOrdersJob(jobData)
					console.log('Job started response:', response)

					// Emit event so parent can update its job state and start polling
					// isJobRunning will be computed from the updated prop
					this.$emit('job-started', response.jobId, this.model)

				} catch (error) {
					console.error('Failed to start cancellation job:', error)
					this.showError(error?.message || this.$t('text.failedToStartCancellationJob'))
				}
			}, 'startCancellationJob')

		},
		convertOrderItemsToOrders(orderItems) {
			// Group order items by order ID
			const ordersMap = {}
			
			for (const item of orderItems) {
				const orderId = parseInt(item.order_id || item.orderId, 10)
				if (!ordersMap[orderId]) {
					ordersMap[orderId] = {
						id: orderId,
						orderItems: []
					}
				}
				
				ordersMap[orderId].orderItems.push({
					id: parseInt(item.order_item_id || item.id, 10),
					orderId: orderId,
					status: item.status || { ordered: 1, shipped: 0, canceled: 0, invoiced: 0, refunded: 0 }
				})
			}
			
			return Object.values(ordersMap)
		},
		getOrderItemStatus(item) {
			// First, check database status to see if item is already canceled/refunded
			if (item.status && (item.status.canceled === 1 || item.status.refunded === 1)) {
				return 'cancelled'
			}
			
			// Then check if this item has a completed status from the background job (for restarts)
			if (this.currentJob?.statusData?.tasks) {
				const task = this.currentJob.statusData.tasks.find(t => 
					t.orderItemId === parseInt(item.order_item_id || item.id, 10)
				)
				if (task) {
					// If job is running and this is a failed task, check if it's currently being reprocessed
					if (task.status === 'ERROR' && this.isJobRunning) {
						const currentOrderId = this.currentJob.statusData.currentOrderId
						const itemOrderId = parseInt(item.order_id || item.orderId, 10)
						
						// If this failed item is in the order currently being processed, show as processing
						if (currentOrderId === itemOrderId) {
							return 'processing'
						}
						
						// Otherwise show as failed (not yet reprocessed)
						return 'failed'
					}
					
					// Return the actual status for completed tasks
					return task.status === 'OK' ? 'cancelled' : 'failed'
				}
			}
			
			// Normal job execution flow: If no task found (i.e. not a restart) and job is running, this item is pending to be processed
			if (this.isJobRunning) {
				const currentOrderId = this.currentJob?.statusData?.currentOrderId
				const itemOrderId = parseInt(item.order_id || item.orderId, 10)
				
				// If this item is in the order currently being processed, show as processing
				if (currentOrderId === itemOrderId) {
					return 'processing'
				}
				
				// Otherwise pending
				return 'pending'
			}
			
			// Initial state: If no task found (i.e. not a restart) and job is not running, this item is pending to be processed
			return 'pending'
		},
		getOrderItemClass(item) {
			const status = this.getOrderItemStatus(item)
			return {
				'order-pending': status === 'pending',
				'order-processing': status === 'processing',
				'order-cancelled': status === 'cancelled',
				'order-failed': status === 'failed'
			}
		},
		getStatusColor(status) {
			switch (status) {
				case 'pending': return 'grey'
				case 'processing': return 'blue'
				case 'cancelled': return 'green'
				case 'failed': return 'red'
				default: return 'grey'
			}
		},
		getStatusLabel(status) {
			switch (status) {
				case 'pending': return this.$t('text.pending')
				case 'processing': return this.$t('text.processing')
				case 'cancelled': return this.$t('text.Cancelled')
				case 'failed': return this.$t('text.failed')
				default: return this.$t('text.unknown')
			}
		},
		getCustomerName(item) {
			if (item.customer) {
				return `${item.customer.firstname || ''} ${item.customer.lastname || ''}`.trim()
			}
			return item.customer_name || this.$t('text.unknownCustomer')
		},
		formatDate(dateString) {
			if (!dateString) return ''
			const date = new Date(dateString)
			return date.toLocaleDateString('de-DE', {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit'
			})
		},
		formatPrice(price, currency) {
			if (!price || price === '0') return 'Free'
			return `${currency || 'EUR'} ${price}`
		},
		formatPaymentInfo(item) {
			if (item.payment_merchant === 'free') return 'Free'
			if (item.payment_method && item.payment_merchant) {
				return `${item.payment_method} (${item.payment_merchant})`
			}
			return item.payment_method || item.payment_merchant || 'N/A'
		},
		getTaskErrorCode(item) {
			if (this.currentJob?.statusData?.tasks) {
				const task = this.currentJob.statusData.tasks.find(t => 
					t.orderItemId === parseInt(item.order_item_id || item.id, 10)
				)
				if (task && task.status === 'ERROR') {
					return task.code || 'unknown_error'
				}
			}
			return ''
		},
		getTaskErrorMessage(item) {
			if (this.currentJob?.statusData?.tasks) {
				const task = this.currentJob.statusData.tasks.find(t => 
					t.orderItemId === parseInt(item.order_item_id || item.id, 10)
				)
				if (task && task.status === 'ERROR') {
					// Get message in current locale if available
					const message = task.message
					if (message && typeof message === 'object') {
						return message[this.selectedLocale] || message.en || this.$t('text.cancellationFailed')
					}
					return message || this.$t('text.cancellationFailed')
				}
			}
			return ''
		},

		async refreshOrderItems() {
			// Refresh order items by calling the parent's getOrderItems function
			// The parent will update its orderItems data, which will flow back down
			// as a prop and trigger our watcher to update orderItems
			if (this.getOrderItems) {
				try {
					await this.getOrderItems()
					console.log('Triggered order items refresh from parent')
				} catch (error) {
					console.error('Failed to refresh order items:', error)
					// Don't throw - this is a background refresh
				}
			}
		},

		async downloadJobResultsExcel() {
			try {
				// Use the already loaded order items from the UI
				if (!this.orderItems || this.orderItems.length === 0) {
					this.showError(this.$t('text.noOrderItemsToExport'))
					return
				}

				// Prepare parameters
				const productName = this.product?.fields?.title?.de || this.product?.fields?.title?.en || 'Product'
				const eventDate = this.selectedDay?.date
				const timeSlot = `${this.selectedSlot?.time || '00:00'}:00`

				// Call ContractExecutive method directly with existing data and job info
				const executive = new BulkCancellationExecutive(this)
				const buffer = await executive.exportOrderItemsToExcel(
					this.orderItems, // Use existing data instead of fetching again
					productName,
					eventDate,
					timeSlot,
					this.currentJob // Pass job data for cancellation status calculation
				)

				// Create blob and trigger download
				const blob = new Blob([buffer], { 
					type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
				})
				
				const url = window.URL.createObjectURL(blob)
				const link = document.createElement('a')
				link.href = url
				link.download = `cancelled-order-items-${eventDate}-${this.selectedSlot?.time || 'all'}.xlsx`
				document.body.appendChild(link)
				link.click()
				document.body.removeChild(link)
				window.URL.revokeObjectURL(url)
				
				this.showSuccess(this.$t('text.excelDownloadStarted'))
			} catch (error) {
				console.error('Failed to download Excel:', error)
				this.showError(this.$t('text.failedToDownloadExcel'))
			}
		},

		handleRestartClick() {
			// Navigate to restart configuration step
			this.currentStep = 'restart-config'
		},
		cleanup() {
			// Reset dialog state
			this.currentStep = 'configure'
			// isJobRunning is computed from prop, no need to reset
		},
		handleDialogClosed() {
			// When dialog is closed, emit event to parent to refresh order items
			console.log('OrderCancellationDialog: Dialog closed, emitting dialog-closed event')
			this.$emit('dialog-closed')
		}
	},
	beforeUnmount() {
		this.cleanup()
	}
}
</script>

<style scoped>
.warning-alert {
	margin: 0;
}

.warning-text {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.warning-title {
	font-weight: 600;
	font-size: 16px;
	margin-bottom: 8px;
	color: #e65100;
}

.warning-message {
	font-size: 14px;
	line-height: 1.5;
	color: #666;
}

.order-info {
	background: #f5f5f5;
	border-radius: 4px;
	padding: 12px 16px;
	border-left: 3px solid #2196f3;
	margin-bottom: 20px;
}

.info-inline {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
}

.info-label {
	font-weight: 600;
	color: #666;
	font-size: 13px;
}

.info-value {
	color: #333;
	font-size: 14px;
	font-weight: 500;
}

.label {
	font-weight: 500;
	color: #666;
}

.value {
	font-weight: 600;
}

.value.success {
	color: #2e7d32;
}

.value.error {
	color: #d32f2f;
}

.force-refund-option {
	background: #f9f9f9;
	border-radius: 8px;
	padding: 16px;
}

.progress-section {
	background: #f5f5f5;
	border-radius: 8px;
	padding: 20px;
}

.orders-container {
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	background: white;
}

.order-item {
	border-bottom: 1px solid #f0f0f0;
	transition: all 0.3s ease;
}

.order-item:last-child {
	border-bottom: none;
}

.order-item.order-pending {
	opacity: 0.6;
	background: #f9f9f9;
}

.order-item.order-processing {
	background: #e3f2fd;
	border-left: 4px solid #2196f3;
}

.order-item.order-cancelled {
	background: #e8f5e8;
	border-left: 4px solid #4caf50;
}

.order-item.order-failed {
	background: #ffebee;
	border-left: 4px solid #f44336;
}

.order-info {
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.order-header {
	display: flex;
	gap: 12px;
	align-items: center;
}

.order-id {
	font-weight: 600;
	font-size: 14px;
	color: #333;
}

.order-item-id {
	font-size: 12px;
	color: #999;
	background: #f5f5f5;
	padding: 2px 6px;
	border-radius: 4px;
}

.order-details {
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.customer-info, .product-info, .order-meta {
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 13px;
}

.customer-name {
	font-weight: 500;
	color: #555;
}

.product-name {
	color: #666;
	flex: 1;
}

.price {
	font-weight: 500;
	color: #333;
	background: #f0f0f0;
	padding: 2px 6px;
	border-radius: 4px;
	font-size: 12px;
}

.order-date, .payment-info {
	font-size: 12px;
	color: #999;
}

.order-meta {
	justify-content: space-between;
}

.order-status {
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: 8px;
	min-width: 120px;
}

.error-message {
	font-size: 11px;
	color: #d32f2f;
	text-align: right;
	max-width: 120px;
	word-wrap: break-word;
}

.no-orders, .loading-orders {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 40px;
	color: #666;
}

.detail-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 8px 0;
	border-bottom: 1px solid #f5f5f5;
}

.detail-row:last-child {
	border-bottom: none;
}

.detail-row .label {
	font-weight: 500;
	color: #666;
	min-width: 100px;
}

.detail-row .value {
	color: #333;
	text-align: right;
}

.detail-row.error-row .value.error-text {
	color: #d32f2f;
	font-weight: 500;
}

.completion-summary {
	margin-top: 20px;
}

.restart-action-section {
	margin-top: 20px;
}

.restart-action-card {
	border: 2px solid #ff9800;
	border-radius: 12px;
	background: linear-gradient(135deg, #fff8e1 0%, #ffffff 100%);
	cursor: pointer;
	transition: all 0.3s ease;
	overflow: hidden;
}

.restart-action-card:hover {
	border-color: #f57c00;
	box-shadow: 0 4px 20px rgba(255, 152, 0, 0.2);
	transform: translateY(-2px);
}

.restart-action-content {
	display: flex;
	align-items: center;
	gap: 16px;
	padding: 20px 24px;
}

.restart-action-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 56px;
	height: 56px;
	background: white;
	border-radius: 50%;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.restart-action-text {
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.restart-action-title {
	font-weight: 600;
	font-size: 16px;
	color: #e65100;
}

.restart-action-description {
	font-size: 14px;
	color: #666;
}

.restart-action-arrow {
	display: flex;
	align-items: center;
}

.restart-action-stats {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 24px;
	padding: 16px 24px;
	background: rgba(255, 255, 255, 0.8);
	border-top: 1px solid rgba(255, 152, 0, 0.2);
}

.stat-item {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 4px;
}

.stat-item .stat-number {
	font-size: 24px;
	font-weight: 700;
}

.stat-item.success .stat-number {
	color: #4caf50;
}

.stat-item.error .stat-number {
	color: #f44336;
}

.stat-item .stat-label {
	font-size: 12px;
	color: #666;
	text-transform: uppercase;
	font-weight: 500;
}

.stat-divider {
	width: 1px;
	height: 40px;
	background: #ddd;
}

.options-section {
	display: flex;
	flex-direction: column;
	gap: 16px;
}

@keyframes spin {
	from { transform: rotate(0deg); }
	to { transform: rotate(360deg); }
}

.mdi-spin {
	animation: spin 1s linear infinite;
}

.export-excel-btn {
	font-weight: 500;
	text-transform: none;
	letter-spacing: 0.5px;
}

.export-excel-btn .v-icon {
	margin-right: 8px;
}
</style>
